import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Loader2, CheckCircle, LogOut, Home, Clock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import SEO from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useAuth } from "@/hooks/useAuth";
import { isValidMobilePhone, PHONE_EXAMPLES, validateAndNormalizeByCountry, type PhoneCountryCode } from "@/lib/phone";
import { PhoneCountryInput } from "@/components/PhoneCountryInput";
import { CHARTE_DECLARATION_TEXT } from "@/lib/charter";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetIdOrContainer: HTMLElement | string) => void;
      reset: (widgetIdOrContainer: HTMLElement | string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}


const inscriptionSchema = z.object({
  nom: z
    .string()
    .trim()
    .min(2, { message: "Le nom doit contenir au moins 2 caractères" })
    .max(100, { message: "Le nom ne peut pas dépasser 100 caractères" })
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, { message: "Le nom ne peut contenir que des lettres" }),
  prenom: z
    .string()
    .trim()
    .min(2, { message: "Le prénom doit contenir au moins 2 caractères" })
    .max(100, { message: "Le prénom ne peut pas dépasser 100 caractères" })
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, { message: "Le prénom ne peut contenir que des lettres" }),
  email: z
    .string()
    .trim()
    .email({ message: "Adresse email invalide" })
    .max(255, { message: "L'email ne peut pas dépasser 255 caractères" }),
  joinGroup: z.enum(["oui", "non"], {
    required_error: "Veuillez indiquer si vous souhaitez rejoindre le groupe WhatsApp / Signal",
  }),
  telephone: z
    .string()
    .trim()
    .optional(),
  referralSource: z.enum(["moteur_recherche", "bouche_oreille", "presse", "reseaux_sociaux", "autre"], {
    required_error: "Veuillez indiquer comment vous avez entendu parler du collectif",
  }),
  referralSourceOther: z.string().trim().max(200).optional(),
  certification: z.boolean().refine((val) => val === true, {
    message: "Vous devez certifier sur l'honneur l'exactitude de vos déclarations",
  }),
  dataConsent: z.boolean().refine((val) => val === true, {
    message: "Vous devez accepter le traitement de vos données",
  }),
  charterConsent: z.boolean().refine((val) => val === true, {
    message: "Vous devez accepter la charte du Collectif",
  }),
}).refine((data) => {
  // If joining group, phone is required and must be a valid FR/BE/CH/LU mobile
  if (data.joinGroup === "oui") {
    if (!data.telephone) return false;
    return isValidMobilePhone(data.telephone);
  }
  return true;
}, {
  message: `Numéro de téléphone mobile invalide. ${PHONE_EXAMPLES}`,
  path: ["telephone"],
}).refine((data) => {
  // If "autre" is selected, the other field must be filled
  if (data.referralSource === "autre") {
    return data.referralSourceOther && data.referralSourceOther.length > 0;
  }
  return true;
}, {
  message: "Veuillez préciser comment vous avez entendu parler du collectif",
  path: ["referralSourceOther"],
});

const Inscription = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<{ message: string; code: string | null; requestId: string | null; status: number | null } | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedData, setSubmittedData] = useState<z.infer<typeof inscriptionSchema> | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [checkingMembership, setCheckingMembership] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>("");

  const referralSourceLabels: Record<string, string> = {
    moteur_recherche: "Moteur de recherche",
    bouche_oreille: "Bouche-à-oreille",
    presse: "Presse / Média",
    reseaux_sociaux: "Réseaux sociaux",
    autre: "Autre",
  };

  // Check if user is already a member
  useEffect(() => {
    const checkMembership = async () => {
      if (!user) {
        setCheckingMembership(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('member_profiles')
          .select('access_validated')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!error && data?.access_validated) {
          setIsMember(true);
        }
      } catch (err) {
        console.error('Error checking membership:', err);
      } finally {
        setCheckingMembership(false);
      }
    };

    checkMembership();
  }, [user]);

  // Load Turnstile widget
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    let cancelled = false;
    let pollId: number | undefined;
    let rafId: number | undefined;

    const renderWidget = () => {
      if (cancelled) return;
      if (widgetIdRef.current) return;
      if (!window.turnstile) return;
      // Wait for the container div to be mounted
      if (!turnstileRef.current) {
        rafId = window.requestAnimationFrame(renderWidget);
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
        });
      } catch (err) {
        console.error('Turnstile render failed', err);
      }
    };

    const waitForTurnstile = () => {
      if (cancelled) return;
      if (window.turnstile) {
        renderWidget();
        return;
      }
      pollId = window.setTimeout(waitForTurnstile, 150);
    };

    // Expose a global onload callback (Turnstile script will call it once ready)
    const previousOnLoad = window.onTurnstileLoad;
    window.onTurnstileLoad = () => {
      previousOnLoad?.();
      renderWidget();
    };

    if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
      script.async = true;
      script.defer = true;
      script.onerror = () => console.error('Turnstile script failed to load');
      document.body.appendChild(script);
    }

    // Fallback: poll in case the script was already loaded (cached) and onload won't fire again
    waitForTurnstile();

    return () => {
      cancelled = true;
      if (pollId) clearTimeout(pollId);
      if (rafId) cancelAnimationFrame(rafId);
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryCode>("FR");
  const [phoneLocal, setPhoneLocal] = useState("");

  const form = useForm<z.infer<typeof inscriptionSchema>>({
    resolver: zodResolver(inscriptionSchema),
    defaultValues: {
      nom: "",
      prenom: "",
      email: "",
      joinGroup: undefined,
      telephone: "",
      referralSource: undefined,
      referralSourceOther: "",
      certification: false,
      dataConsent: false,
      charterConsent: false,
    },
  });

  const onSubmit = async (values: z.infer<typeof inscriptionSchema>) => {
    setSubmitError(null);

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Veuillez compléter la vérification de sécurité ci-dessus avant de valider.');
      return;
    }

    setLoading(true);

    try {
      // Check for duplicate email client-side before calling edge function
      const { data: existingEmails, error: checkError } = await supabase
        .from('inscriptions')
        .select('email')
        .eq('email', values.email.toLowerCase().trim())
        .limit(1);

      if (!checkError && existingEmails && existingEmails.length > 0) {
        form.setError('email', {
          type: 'manual',
          message: 'Cette adresse email est déjà inscrite. Si vous souhaitez modifier votre inscription ou si vous pensez qu\'il s\'agit d\'une erreur, contactez-nous.',
        });
        toast.error('Cette adresse email est déjà associée à une inscription.');
        setLoading(false);
        if (window.turnstile && turnstileRef.current) {
          window.turnstile.reset(turnstileRef.current);
        }
        return;
      }

      // Call edge function with Turnstile token
      const { data, error } = await supabase.functions.invoke('inscription', {
        body: { ...values, turnstileToken },
      });

      // Helper: extract the structured error body { error, code, requestId }
      // from a FunctionsHttpError (supabase-js wraps non-2xx responses).
      const extractServerError = async (
        err: any
      ): Promise<{ message: string | null; code: string | null; requestId: string | null; status: number | null }> => {
        const out = { message: null as string | null, code: null as string | null, requestId: null as string | null, status: null as number | null };
        try {
          const resp = err?.context as Response | undefined;
          if (resp) {
            out.status = typeof resp.status === 'number' ? resp.status : null;
            if (typeof resp.json === 'function') {
              try {
                const cloned = typeof resp.clone === 'function' ? resp.clone() : resp;
                const body = await cloned.json();
                if (body?.error) out.message = String(body.error);
                if (body?.code) out.code = String(body.code);
                if (body?.requestId) out.requestId = String(body.requestId);
              } catch {
                try {
                  const cloned2 = typeof resp.clone === 'function' ? resp.clone() : resp;
                  const text = await cloned2.text();
                  if (text) {
                    try {
                      const j = JSON.parse(text);
                      if (j?.error) out.message = String(j.error);
                      if (j?.code) out.code = String(j.code);
                      if (j?.requestId) out.requestId = String(j.requestId);
                    } catch {
                      out.message = text;
                    }
                  }
                } catch {}
              }
            }
          }
        } catch {}
        return out;
      };

      // Check if the response contains an error message (200 path with { error })
      if (data?.error) {
        const isDuplicate = String(data.error).toLowerCase().includes('déjà inscrit') || data.code === 'EMAIL_ALREADY_REGISTERED';
        if (isDuplicate) {
          form.setError('email', {
            type: 'manual',
            message: 'Cette adresse email est déjà inscrite. Si vous souhaitez modifier votre inscription ou si vous pensez qu\'il s\'agit d\'une erreur, contactez-nous.',
          });
        }
        setSubmitError({ message: data.error, code: data.code ?? null, requestId: data.requestId ?? null, status: 200 });
        toast.error(isDuplicate ? 'Cet email est déjà inscrit.' : data.error);
        setLoading(false);
        setTurnstileToken('');
        if (window.turnstile && turnstileRef.current) {
          window.turnstile.reset(turnstileRef.current);
        }
        return;
      }

      // Non-2xx path: extract real message from the wrapped error
      if (error) {
        const { message: serverMsg, code, requestId, status } = await extractServerError(error);
        const description = serverMsg || error.message || "Une erreur est survenue lors de l'inscription.";
        const isDuplicate = code === 'EMAIL_ALREADY_REGISTERED' || description.toLowerCase().includes('déjà inscrit');
        const isTurnstile = (code ?? '').startsWith('TURNSTILE_') || description.toLowerCase().includes('vérification de sécurité');

        setSubmitError({ message: description, code, requestId, status });

        if (isDuplicate) {
          form.setError('email', {
            type: 'manual',
            message: 'Cette adresse email est déjà inscrite. Si vous souhaitez modifier votre inscription ou si vous pensez qu\'il s\'agit d\'une erreur, contactez-nous.',
          });
          toast.error('Cet email est déjà inscrit.');
        } else if (isTurnstile) {
          toast.error('La vérification de sécurité a expiré ou échoué. La case « Je ne suis pas un robot » a été réinitialisée — merci de la recocher puis de valider à nouveau.');
        } else {
          toast.error(description);
        }
        setLoading(false);
        setTurnstileToken('');
        if (window.turnstile && turnstileRef.current) {
          window.turnstile.reset(turnstileRef.current);
        }
        return;
      }

      // Success: save submitted data, reset form and show confirmation dialog
      setSubmittedData(values);
      form.reset();
      setTurnstileToken('');
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.reset(turnstileRef.current);
      }
      setShowConfirmation(true);
    } catch (error: any) {
      console.error('Error:', error);
      const msg = error?.message || "Une erreur est survenue lors de l'inscription.";
      setSubmitError({ message: msg, code: 'CLIENT_EXCEPTION', requestId: null, status: null });
      toast.error(msg);
      setTurnstileToken('');
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.reset(turnstileRef.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking membership
  if (checkingMembership) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  // Show message if user is already a member
  if (user && isMember) {
    return (
      <>
        <SEO
          title="Vous êtes déjà membre - Collectif Ibbü"
          description="Vous êtes déjà membre du Collectif Ibbü."
          canonical="https://collectif-ibbu.org/inscription"
        />
        <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4">
          <div className="max-w-xl mx-auto space-y-8">
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Retour à l'accueil
              </Button>
            </Link>

            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <CardTitle className="text-2xl">Vous êtes déjà membre !</CardTitle>
                <CardDescription className="text-base mt-2">
                  Ce formulaire d'inscription est réservé aux nouveaux membres. Vous avez déjà un compte validé au sein du Collectif Ibbü.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="flex flex-col gap-3">
                  <Button asChild className="w-full gap-2">
                    <Link to="/espace-membre/dashboard">
                      <Home className="h-4 w-4" />
                      Accéder à mon espace membre
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={async () => {
                      await signOut();
                      navigate('/inscription');
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    Me déconnecter pour inscrire quelqu'un d'autre
                  </Button>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-4">
                  Si vous souhaitez inscrire une autre personne au collectif, vous devez d'abord vous déconnecter de votre compte actuel.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  const watchReferralSource = form.watch("referralSource");

  return (
    <>
      <SEO
        title="Inscription | Collectif Ibbü - Rejoignez l'action"
        description="Rejoignez l'action collective du Collectif Ibbü. Inscription gratuite et confidentielle pour les conseillers iAdvize/Ibbu."
        canonical="https://collectif-ibbu.org/inscription"
        keywords="Ibbü, Ibbu, iAdvize, inscription, rejoindre, conseillers, action collective"
      />
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Inscription reçue</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {/* Status badge */}
                <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  <Clock className="h-5 w-5 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">Inscription en attente de validation</p>
                    <p className="text-amber-800/90">
                      Délai moyen : 24 à 48h ouvrées. Vous recevrez un email de confirmation dès validation.
                    </p>
                  </div>
                </div>

                {/* Recap */}
                {submittedData && (
                  <div className="rounded-md border border-border bg-muted/30 p-4">
                    <p className="text-sm font-semibold text-foreground mb-3">
                      Récapitulatif de votre inscription
                    </p>
                    <dl className="grid grid-cols-1 gap-y-2 text-sm">
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">Prénom</dt>
                        <dd className="font-medium text-foreground text-right break-words">{submittedData.prenom}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">Nom</dt>
                        <dd className="font-medium text-foreground text-right break-words">{submittedData.nom}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">Email</dt>
                        <dd className="font-medium text-foreground text-right break-all">{submittedData.email}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">Groupe WhatsApp</dt>
                        <dd className="font-medium text-foreground text-right">
                          {submittedData.joinGroup === "oui" ? "Oui" : "Non"}
                        </dd>
                      </div>
                      {submittedData.joinGroup === "oui" && submittedData.telephone && (
                        <div className="flex justify-between gap-3">
                          <dt className="text-muted-foreground">Téléphone</dt>
                          <dd className="font-medium text-foreground text-right">{submittedData.telephone}</dd>
                        </div>
                      )}
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">Source</dt>
                        <dd className="font-medium text-foreground text-right break-words">
                          {referralSourceLabels[submittedData.referralSource] || submittedData.referralSource}
                          {submittedData.referralSource === "autre" && submittedData.referralSourceOther
                            ? ` — ${submittedData.referralSourceOther}`
                            : ""}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => navigate("/")}>Retour à l'accueil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <main className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>

          <Breadcrumbs items={[{ label: "Inscription" }]} />

          {/* Page Header with H1 */}
          <div className="text-center space-y-4 mb-8">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              Rejoindre l'action collective
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Remplissez le formulaire ci-dessous pour vous inscrire au Collectif Ibbü
            </p>
          </div>

          {/* Form Card */}
          <Card className="border-0 shadow-xl animate-fade-in">
            <CardContent className="pt-8">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="prenom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">
                          Prénom <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Votre prénom" className="h-12" disabled={loading} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="nom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">
                          Nom <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Votre nom" className="h-12" disabled={loading} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">
                          Email <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="votre.email@example.com"
                            className="h-12"
                            disabled={loading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="joinGroup"
                    render={({ field }) => (
                      <FormItem className="space-y-3 pt-2">
                        <FormLabel className="text-base">
                          Souhaitez-vous intégrer le groupe WhatsApp ? <span className="text-destructive">*</span>
                          <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            Fortement recommandé
                          </span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup value={field.value} onValueChange={field.onChange} disabled={loading}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="oui" id="join-yes" />
                              <Label htmlFor="join-yes" className="font-normal cursor-pointer">
                                Oui
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="non" id="join-no" />
                              <Label htmlFor="join-no" className="font-normal cursor-pointer">
                                Non
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <div className="mt-3 p-4 bg-amber-50/80 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                          <p className="text-sm text-foreground leading-relaxed">
                            <span className="font-semibold text-amber-700 dark:text-amber-300">💬 L'intégration au groupe est fortement recommandée</span>
                            <br />
                            C'est sur WhatsApp que se fait la majorité de la communication au sein du collectif.
                            Si vous choisissez "Oui", votre numéro de téléphone sera utilisé uniquement pour vous ajouter au groupe.
                          </p>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch("joinGroup") === "oui" && (
                    <FormField
                      control={form.control}
                      name="telephone"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-base">
                            Numéro de téléphone mobile <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <PhoneCountryInput
                              country={phoneCountry}
                              onCountryChange={setPhoneCountry}
                              localValue={phoneLocal}
                              onLocalChange={(local, normalized) => {
                                setPhoneLocal(local);
                                field.onChange(normalized ?? local);
                              }}
                              disabled={loading}
                              invalid={!!fieldState.error}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            Sélectionnez votre pays puis saisissez votre numéro mobile.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="referralSource"
                    render={({ field }) => (
                      <FormItem className="space-y-3 pt-2">
                        <FormLabel className="text-base">
                          Comment avez-vous entendu parler du collectif ? <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup value={field.value} onValueChange={field.onChange} disabled={loading}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="moteur_recherche" id="referral-search" />
                              <Label htmlFor="referral-search" className="font-normal cursor-pointer">
                                Moteur de recherche (Google, Bing, etc.)
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="bouche_oreille" id="referral-word" />
                              <Label htmlFor="referral-word" className="font-normal cursor-pointer">
                                Bouche à oreille
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="presse" id="referral-press" />
                              <Label htmlFor="referral-press" className="font-normal cursor-pointer">
                                Presse / Médias
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="reseaux_sociaux" id="referral-social" />
                              <Label htmlFor="referral-social" className="font-normal cursor-pointer">
                                Réseaux sociaux
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="autre" id="referral-other" />
                              <Label htmlFor="referral-other" className="font-normal cursor-pointer">
                                Autre
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {watchReferralSource === "autre" && (
                    <FormField
                      control={form.control}
                      name="referralSourceOther"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base">
                            Précisez <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Comment avez-vous découvert le collectif ?"
                              className="h-12"
                              disabled={loading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <div className="space-y-4 pt-4 border-t border-border">
                    <FormField
                      control={form.control}
                      name="certification"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={loading} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-normal leading-tight cursor-pointer">
                              Je certifie sur l'honneur l'exactitude de mes déclarations, notamment concernant mon
                              activité en tant que conseiller Ibbü/iAdvize. Je reconnais que toute fausse déclaration
                              peut entraîner mon exclusion du collectif.<span className="text-destructive">*</span>
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dataConsent"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={loading} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-normal leading-tight cursor-pointer">
                              J'accepte que les informations que je fournis soient collectées et traitées par le
                              collectif dans le but de m'informer, de partager des témoignages et d'organiser une
                              démarche juridique groupée. Je comprends que mes données pourront être communiquées, avec
                              mon accord explicite ultérieur, pour faciliter une mise en relation avec un avocat
                              spécialisé, conformément à notre{" "}
                              <Link
                                to="/confidentialite"
                                target="_blank"
                                className="text-primary hover:underline font-medium"
                              >
                                Politique de Confidentialité
                              </Link>
                              .{" "}
                              <span className="text-destructive">*</span>
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="charterConsent"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={loading} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-normal leading-tight cursor-pointer">
                              {(() => {
                                const marker = " charte ";
                                const idx = CHARTE_DECLARATION_TEXT.indexOf(marker);
                                const before = CHARTE_DECLARATION_TEXT.slice(0, idx + 1);
                                const after = CHARTE_DECLARATION_TEXT.slice(idx + marker.length - 1);
                                return (
                                  <>
                                    {before}
                                    <a
                                      href="/charte"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline font-medium"
                                    >
                                      charte
                                    </a>
                                    {after}
                                  </>
                                );
                              })()}
                              <span className="text-destructive"> *</span>
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>

                  {TURNSTILE_SITE_KEY && (
                    <div ref={turnstileRef} className="flex justify-center" />
                  )}

                  {submitError && (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm"
                    >
                      <p className="font-semibold text-destructive">
                        Échec de l'inscription
                        {submitError.status ? ` (HTTP ${submitError.status})` : ""}
                      </p>
                      <p className="mt-1 text-destructive/90">{submitError.message}</p>
                      {(submitError.code || submitError.requestId) && (
                        <p className="mt-2 font-mono text-xs text-muted-foreground">
                          {submitError.code ? `code: ${submitError.code}` : ""}
                          {submitError.code && submitError.requestId ? " · " : ""}
                          {submitError.requestId ? `requestId: ${submitError.requestId}` : ""}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        Si le problème persiste, transmettez ces informations à{" "}
                        <a href="mailto:collectif@collectif-ibbu.org" className="underline">
                          collectif@collectif-ibbu.org
                        </a>
                        .
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                    disabled={loading || (TURNSTILE_SITE_KEY ? !turnstileToken : false)}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Inscription en cours...
                      </>
                    ) : (
                      "Valider mon inscription"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Info Box */}
          <Card className="border-accent/50 bg-accent/5">
            <CardContent className="p-6">
              <p className="text-sm text-center text-muted-foreground leading-relaxed">
                Vos données sont confidentielles et ne seront utilisées que pour les objectifs de notre démarche
                collective, conformément à notre{" "}
                <Link to="/confidentialite" className="text-primary underline hover:text-primary/80" target="_blank">
                  Politique de Confidentialité
                </Link>
                .
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

export default Inscription;
